import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "@/store";
import {
  GetCorrespondentTransactionHistoryResponse,
  getTransactionHistory,
} from "@/api/custom";

type TransactionState = {
  entries: Array<{
    description: string;
    amount: number;
    created_at: string;
    state: string;
    fee_type?: string;
    account_id?: string;
  }>;
  current_balance: number;
};

export type ApexFeeState =
  | {
      fetchState: "not-started";
    }
  | {
      fetchState: "fetching";
      feeState?: TransactionState;
      lastFetched?: number;
    }
  | {
      fetchState: "success";
      feeState: TransactionState;
      lastFetched: number;
    }
  | {
      fetchState: "error";
      errorMessage: string;
      feeState?: TransactionState;
      lastFetched?: number;
    };

const initialState: ApexFeeState = {
  fetchState: "not-started",
};

const apexFeesSlice = createSlice({
  name: "ApexFees",
  initialState: initialState satisfies ApexFeeState as ApexFeeState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchAllApexFeesThunk.fulfilled, (state, action) => {
        state = {
          fetchState: "success",
          feeState: action.payload,
          lastFetched: new Date().getTime(),
        };
        console.debug("Success: ", action.payload);
        return state;
      })
      .addCase(fetchAllApexFeesThunk.pending, (state) => {
        state = {
          ...state,
          fetchState: "fetching",
        };
        return state;
      })
      .addCase(fetchAllApexFeesThunk.rejected, (state, action) => {
        state = {
          ...state,
          fetchState: "error",
          errorMessage: action.error.message || "Unknown Error",
        };
        return state;
      });
  },
});

export const selectApexFees = (state: RootState) => state.apexFees;

export const fetchAllApexFeesThunk = createAsyncThunk(
  "apexFees/fetchAll",
  async () => {
    const entries: Array<TransactionState["entries"][number]> = [];

    async function pushEntries(
      response: GetCorrespondentTransactionHistoryResponse,
    ) {
      entries.push(
        ...response.collected_fees.map((f) => {
          const coefficient = (100 - parseFloat(f.platform_portion)) / 100;
          const amount = parseFloat(f.amount) * coefficient;
          return {
            amount: amount,
            description: f.description,
            created_at: f.created_at,
            account_id: f.account_id,
            state:
              f.state === "COMPLETED"
                ? "completed"
                : f.state === "REJECTED" ||
                    f.state === "CANCELED" ||
                    f.state === "RETURNED"
                  ? "failed"
                  : "pending",
          };
        }),
      );
    }

    async function pushWithdrawals(
      response: GetCorrespondentTransactionHistoryResponse,
    ) {
      entries.push(
        ...response.withdrawals
          .filter((v) => !v.is_platform)
          .map((v) => {
            return {
              amount: -parseFloat(v.amount),
              description: "Withdrawal",
              created_at: v.created_at,
              state:
                v.state === "COMPLETED"
                  ? "completed"
                  : v.state === "REJECTED" ||
                      v.state === "CANCELED" ||
                      v.state === "RETURNED"
                    ? "failed"
                    : "pending",
            };
          }),
      );
    }

    let response = await getTransactionHistory(null, 1000);
    pushEntries(response);
    pushWithdrawals(response);

    while (response.fee_list_pagination_key) {
      response = await getTransactionHistory(
        response.fee_list_pagination_key,
        1000,
      );
      pushEntries(response);
    }

    entries.sort((a, b) => dateStringCompare(a.created_at, b.created_at));
    const transactionState: TransactionState = {
      current_balance: parseFloat(response.current_balance),
      entries,
    };
    return transactionState;
  },
);

function dateStringCompare(a: string, b: string) {
  return new Date(b).getTime() - new Date(a).getTime();
}

export const apexFeesReducer = apexFeesSlice.reducer;
